const { config, collection, fields } = require('@keystatic/core');
const { wrapper } = require('@keystatic/core/content-components');

const passthroughView = ({ children }) => children;

const insightMarkdocComponents = {
  row: wrapper({
    label: 'Row',
    schema: {
      columns: fields.integer({ label: 'Columns', defaultValue: 2 }),
      reverse: fields.checkbox({ label: 'Reverse', defaultValue: false }),
    },
    ContentView: passthroughView,
  }),
  col: wrapper({
    label: 'Column',
    schema: {
      span: fields.integer({ label: 'Span', defaultValue: 1 }),
    },
    ContentView: passthroughView,
  }),
  table: wrapper({
    label: 'Table Layout',
    schema: {
      columns: fields.integer({ label: 'Columns', defaultValue: 2 }),
    },
    ContentView: passthroughView,
  }),
  callout: wrapper({
    label: 'Callout',
    schema: {
      tone: fields.select({
        label: 'Tone',
        options: [
          { label: 'Info', value: 'info' },
          { label: 'Success', value: 'success' },
          { label: 'Warning', value: 'warning' },
          { label: 'Danger', value: 'danger' },
        ],
        defaultValue: 'info',
      }),
      title: fields.text({ label: 'Title' }),
    },
    ContentView: passthroughView,
  }),
  cards: wrapper({
    label: 'Cards',
    schema: {
      columns: fields.integer({ label: 'Columns', defaultValue: 3 }),
    },
    ContentView: passthroughView,
  }),
  card: wrapper({
    label: 'Card',
    schema: {
      title: fields.text({ label: 'Title' }),
      href: fields.text({ label: 'Link' }),
    },
    ContentView: passthroughView,
  }),
  gallery: wrapper({
    label: 'Gallery',
    schema: {
      columns: fields.integer({ label: 'Columns', defaultValue: 3 }),
    },
    ContentView: passthroughView,
  }),
  stats: wrapper({
    label: 'Stats',
    schema: {
      columns: fields.integer({ label: 'Columns', defaultValue: 3 }),
    },
    ContentView: passthroughView,
  }),
  stat: wrapper({
    label: 'Stat',
    schema: {
      value: fields.text({ label: 'Value' }),
      label: fields.text({ label: 'Label' }),
    },
    ContentView: passthroughView,
  }),
  timeline: wrapper({
    label: 'Timeline',
    schema: {},
    ContentView: passthroughView,
  }),
  timelineItem: wrapper({
    label: 'Timeline Item',
    schema: {
      title: fields.text({ label: 'Title' }),
      meta: fields.text({ label: 'Meta' }),
    },
    ContentView: passthroughView,
  }),
  faq: wrapper({
    label: 'FAQ',
    schema: {},
    ContentView: passthroughView,
  }),
  faqItem: wrapper({
    label: 'FAQ Item',
    schema: {
      question: fields.text({ label: 'Question' }),
      open: fields.checkbox({ label: 'Open by default', defaultValue: false }),
    },
    ContentView: passthroughView,
  }),
  quote: wrapper({
    label: 'Quote',
    schema: {
      author: fields.text({ label: 'Author' }),
      role: fields.text({ label: 'Role' }),
    },
    ContentView: passthroughView,
  }),
  heroSplit: wrapper({
    label: 'Hero Split',
    schema: {
      eyebrow: fields.text({ label: 'Eyebrow' }),
      title: fields.text({ label: 'Title' }),
      subtitle: fields.text({ label: 'Subtitle' }),
      image: fields.text({ label: 'Image path' }),
      imageAlt: fields.text({ label: 'Image alt' }),
      reverse: fields.checkbox({ label: 'Reverse layout', defaultValue: false }),
      ctaText: fields.text({ label: 'CTA text' }),
      ctaHref: fields.text({ label: 'CTA link' }),
    },
    ContentView: passthroughView,
  }),
  featureGrid: wrapper({
    label: 'Feature Grid',
    schema: {
      columns: fields.integer({ label: 'Columns', defaultValue: 3 }),
    },
    ContentView: passthroughView,
  }),
  featureItem: wrapper({
    label: 'Feature Item',
    schema: {
      title: fields.text({ label: 'Title' }),
      icon: fields.text({ label: 'Icon' }),
    },
    ContentView: passthroughView,
  }),
  process: wrapper({
    label: 'Process',
    schema: {
      title: fields.text({ label: 'Title' }),
    },
    ContentView: passthroughView,
  }),
  step: wrapper({
    label: 'Step',
    schema: {
      title: fields.text({ label: 'Title' }),
      number: fields.text({ label: 'Step label' }),
    },
    ContentView: passthroughView,
  }),
  ctaBand: wrapper({
    label: 'CTA Band',
    schema: {
      title: fields.text({ label: 'Title' }),
      buttonText: fields.text({ label: 'Button text' }),
      buttonHref: fields.text({ label: 'Button link' }),
      tone: fields.select({
        label: 'Tone',
        options: [
          { label: 'Primary', value: 'primary' },
          { label: 'Secondary', value: 'secondary' },
        ],
        defaultValue: 'primary',
      }),
    },
    ContentView: passthroughView,
  }),
};

module.exports = config({
  storage: {
    kind: 'github',
    repo: 'tissatechteam/tissawebsite-usa',
    branchPrefix: 'content/',
  },
  ui: {
    brand: { name: 'TISSA Insights CMS' },
  },
  collections: {
    insights: collection({
      label: 'Insights',
      slugField: 'title',
      path: 'content/insights/*',
      format: { contentField: 'content' },
      schema: {
        title: fields.slug({ name: { label: 'Title' } }),
        status: fields.select({
          label: 'Status',
          description: 'Draft = private. In review = waiting for approval. Published = live on the website after merge/deploy. Keystatic saves to the currently selected branch.',
          options: [
            { label: 'Draft', value: 'draft' },
            { label: 'In review', value: 'in_review' },
            { label: 'Published', value: 'published' },
          ],
          defaultValue: 'draft',
        }),
        publishedDate: fields.date({
          label: 'Published date',
          defaultValue: { kind: 'today' },
        }),
        category: fields.select({
          label: 'Category',
          options: [
            { label: 'Technology', value: 'technology' },
            { label: 'Industry', value: 'industry' },
            { label: 'Case Study', value: 'case-study' },
            { label: 'Thought Leadership', value: 'thought-leadership' },
          ],
          defaultValue: 'technology',
        }),
        author: fields.text({ label: 'Author' }),
        excerpt: fields.text({
          label: 'Excerpt',
          description: 'Short summary shown on the Insights list page.',
          multiline: true,
        }),
        coverImage: fields.text({
          label: 'Cover image path',
          description: 'Optional public path for the cover image.',
        }),
        ctaText: fields.text({ label: 'CTA button text' }),
        ctaLink: fields.text({ label: 'CTA link' }),
        content: fields.markdoc({
          label: 'Content',
          extension: 'mdoc',
          components: insightMarkdocComponents,
        }),
      },
    }),
  },
});